import { swissRound } from '$lib/utils/swissRounding';
import type { CompanyWithContacts } from '$lib/types/api';

/** One band of the volume price ladder. */
export interface PricingTier {
  /** How many records this band covers. */
  count: number;
  /** Price per record within the band, in CHF. */
  price: number;
}

export const pricingTiers: PricingTier[] = [
  { count: 200, price: 1.9 },
  { count: 300, price: 1.6 },
  { count: 500, price: 0.9 },
  { count: 4000, price: 0.6 },
  { count: Infinity, price: 0.55 }
];

/**
 * The largest selection this app puts a price on.
 *
 * # Why a price stops rather than growing
 *
 * Above this nothing is actually delivered. The guided screen refuses to run a
 * search larger than `resultCap` in stores/guided.ts, and the service trims to
 * `maxPreviewCompanies` in internal/httpapi/selection.go regardless — all three
 * are the same twenty thousand, for the same reason.
 *
 * So a bar reading "CHF 34,000" beside a count of sixty thousand quotes a
 * delivery that cannot happen. It is not merely a large number: it is the wrong
 * number, attached to the one figure on the screen a customer takes literally.
 * Showing the count and leaving the price blank says what is true — this is how
 * many there are, and what it costs is not a question this screen can answer.
 *
 * Keep it in step with the other two. If they drift, the screen either withholds
 * a price for a selection it would happily run, or quotes one for a selection
 * that comes back trimmed.
 */
export const maxQuotableRecords = 30_000;

/**
 * Whether a selection of this size gets a figure beside it.
 *
 * Zero is unquotable for the ordinary reason — there is nothing to price yet,
 * and "CHF 0.00" reads as an answer rather than as an empty screen.
 */
export function quotable(recordCount: number): boolean {
  return recordCount > 0 && recordCount <= maxQuotableRecords;
}

export function calculateTotalPrice(tiers: PricingTier[], recordCount: number): number {
  let total = 0;
  let remaining = recordCount;

  for (let i = 0; i < tiers.length && remaining > 0; i++) {
    const tier = tiers[i];
    const nextCount = tier.count;
    const unitsToCharge = Math.min(remaining, nextCount);
    total += unitsToCharge * tier.price;
    remaining -= unitsToCharge;
  }

  return swissRound(total);
}

/**
 * How many records a selection is charged for.
 *
 * A company with no contact is not free: it costs what a company with one
 * contact costs. So the billable count is the contacts when any were asked
 * for, and the companies when none were — never the sum, and never zero
 * because nobody picked a role.
 *
 * This is the rule the checkout has always applied, extracted so that the
 * guided search screen quotes from it rather than restating it. A screen that
 * priced a selection differently from the checkout would be a quote the
 * checkout does not honour, which is worse than showing no price at all.
 */
export function billableRecords(companies: number, contacts: number): number {
  return contacts > 0 ? contacts : companies;
}

/**
 * Calculate the billable record count based on the query results.
 * @param queryResult - Array of companies with contacts
 * @returns The number of billable records
 */
export function getBillableRecordCount(queryResult: CompanyWithContacts[] | null): number {
  if (!queryResult || queryResult.length === 0) {
    return 0;
  }

  const contactCount = queryResult.reduce((total: number, company: CompanyWithContacts) => {
    return total + (company.contacts?.length || 0);
  }, 0);

  return billableRecords(queryResult.length, contactCount);
}
